import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, SignOut, MagnifyingGlass, X, CaretLeft, CaretRight, NotePencil, Minus, Lightbulb, Image, ImageSquare } from '@phosphor-icons/react';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import Toast from '../components/Toast';
import AnalysisCompare from '../components/AnalysisCompare';
import { KNOWN_SECTIONS, formatSectionLabel } from '../utils/sections';

// Helper to generate sections dynamically from response data
const getSections = (response) => {
  const sections = [{ key: 'core', label: 'Core Fields' }];

  if (!response) return [...sections, { key: 'raw', label: 'Raw JSON' }];

  // Add known sections that exist in response
  if (response.media_metadata) sections.push({ key: 'media_metadata', label: 'Media Metadata' });
  if (response.image_details) sections.push({ key: 'image_details', label: 'Image Details' });
  if (response.enrichment_hint) sections.push({ key: 'enrichment_hint', label: 'Enrichment Hint' });

  // Find any other nested objects not in known sections
  const otherObjects = Object.entries(response)
    .filter(([key, value]) =>
      typeof value === 'object' &&
      value !== null &&
      !Array.isArray(value) &&
      !KNOWN_SECTIONS.includes(key)
    )
    .map(([key]) => key);

  // Add dynamic sections for unknown nested objects
  otherObjects.forEach(key => {
    sections.push({ key, label: formatSectionLabel(key) });
  });

  sections.push({ key: 'raw', label: 'Raw JSON' });
  return sections;
};

// Get all top-level fields that are not nested objects (for Core Fields section)
const getCoreFields = (response) => {
  if (!response) return [];
  return Object.entries(response)
    .filter(([key, value]) => {
      // Include primitives and arrays, exclude nested objects
      if (value === null || value === undefined) return true;
      if (Array.isArray(value)) return true;
      if (typeof value !== 'object') return true;
      return false;
    })
    .map(([key]) => key);
};

// Deep equality check for objects (more efficient than JSON.stringify for change detection)
const deepEqual = (a, b) => {
  if (a === b) return true;
  if (a === null || b === null) return a === b;
  if (typeof a !== 'object' || typeof b !== 'object') return a === b;
  const keysA = Object.keys(a);
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;
  for (const key of keysA) {
    if (!keysB.includes(key) || !deepEqual(a[key], b[key])) return false;
  }
  return true;
};

const ENTITY_TYPES = ['place', 'person', 'organization', 'event', 'creative_work', 'product', 'reference', 'entity'];
const SUBTYPE_SUGGESTIONS = [
  // Places
  'Restaurant', 'Cafe', 'Bar', 'Hotel', 'Museum', 'Park', 'Store', 'Gym', 'Salon', 'Attraction',
  // People
  'Actor', 'Musician', 'Chef', 'Author', 'Athlete', 'Influencer', 'Public Figure',
  // Organizations
  'Brand', 'Company', 'Non-Profit', 'Sports Team', 'Media Outlet',
  // Events
  'Concert', 'Festival', 'Conference', 'Exhibition', 'Premiere', 'Broadcast',
  // Creative works
  'Movie', 'TV Series', 'K-Drama Series', 'Anime Series', 'Album', 'Song', 'Book', 'Podcast', 'Recipe', 'Video Game',
  // Products
  'Physical Product', 'Software', 'App', 'Deal', 'Coupon',
  // References
  'Webpage', 'Article', 'Social Post', 'Profile',
];
const LIKELY_SOURCES = ['TikTok', 'Instagram', 'Pinterest', 'Facebook', 'X', 'LinkedIn', 'Reddit', 'YouTube', 'user-created', 'other'];

// Map of field names to their enum values for auto-detection
const ENUM_FIELDS = {
  likely_source: LIKELY_SOURCES,
  entity_type: ENTITY_TYPES,
};

export default function Review() {
  const { logout } = useAuth();

  // State
  const [items, setItems] = useState([]);
  const [selectedItemId, setSelectedItemId] = useState('');
  const [currentItem, setCurrentItem] = useState(null);
  const [currentAnalysis, setCurrentAnalysis] = useState(null);
  const [originalResponse, setOriginalResponse] = useState(null);
  const [editedResponse, setEditedResponse] = useState(null);

  // Baseline state for tracking actual changes (set after loading annotation)
  const [baselineResponse, setBaselineResponse] = useState(null);
  const [baselineFieldNotes, setBaselineFieldNotes] = useState({});
  const [baselineSessionNotes, setBaselineSessionNotes] = useState('');
  const [baselineMarkAsGolden, setBaselineMarkAsGolden] = useState(false);

  // UI State
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState(null);
  const [activeSection, setActiveSection] = useState('core');
  const [showingNoteFor, setShowingNoteFor] = useState(null);
  const [toast, setToast] = useState(null); // { message, type }
  const [showImageLightbox, setShowImageLightbox] = useState(false);

  // Mode: 'annotate' or 'compare'
  const [mode, setMode] = useState('annotate');

  // Annotation data
  const [fieldNotes, setFieldNotes] = useState({});
  const [sessionNotes, setSessionNotes] = useState('');
  const [markAsGolden, setMarkAsGolden] = useState(false);

  // Memoized sections for tabs (avoids recalculation on each render)
  const sections = useMemo(() => getSections(editedResponse), [editedResponse]);

  // Memoized core fields (avoids recalculation on each render)
  const coreFields = useMemo(() => getCoreFields(editedResponse), [editedResponse]);
  const primitiveFields = useMemo(
    () => coreFields.filter(field => !Array.isArray(editedResponse?.[field])),
    [coreFields, editedResponse]
  );
  const arrayFields = useMemo(
    () => coreFields.filter(field => Array.isArray(editedResponse?.[field])),
    [coreFields, editedResponse]
  );

  // Loading progress for batch item fetch
  const [loadProgress, setLoadProgress] = useState('');
  const cancelledRef = useRef(false);

  // Load ALL items in batches
  const loadItems = useCallback(async () => {
    cancelledRef.current = false;
    setLoading(true);
    setError(null);
    setLoadProgress('Loading items...');
    try {
      const BATCH_SIZE = 100;
      let allItems = [];
      let offset = 0;
      let total = 0;

      const firstResponse = await api.getItems({ limit: BATCH_SIZE, offset: 0 });
      if (cancelledRef.current) return;
      allItems = firstResponse.items || [];
      total = firstResponse.total || allItems.length;
      offset = allItems.length;

      while (offset < total && !cancelledRef.current) {
        setLoadProgress(`Loading items... ${offset}/${total}`);
        const response = await api.getItems({ limit: BATCH_SIZE, offset });
        if (cancelledRef.current) return;
        const batch = response.items || [];
        if (batch.length === 0) break;
        allItems = [...allItems, ...batch];
        offset += batch.length;
      }

      if (!cancelledRef.current) {
        setItems(allItems);
        setLoadProgress('');
      }
    } catch (err) {
      if (!cancelledRef.current) {
        setError('Failed to load items: ' + err.message);
        setLoadProgress('');
      }
    } finally {
      if (!cancelledRef.current) setLoading(false);
    }
  }, []);

  // Load on mount
  useEffect(() => {
    loadItems();
    return () => { cancelledRef.current = true; };
  }, [loadItems]);

  // Clear saved indicator after 3 seconds
  useEffect(() => {
    if (saved) {
      const timer = setTimeout(() => setSaved(false), 3000);
      return () => clearTimeout(timer);
    }
  }, [saved]);

  // Auto-hide toast after 3 seconds
  useEffect(() => {
    if (toast) {
      const timer = setTimeout(() => setToast(null), 3000);
      return () => clearTimeout(timer);
    }
  }, [toast]);

  // Compute hasChanges for unsaved changes warning (using deepEqual for better performance)
  const hasChanges = useMemo(() => {
    if (!baselineResponse) return false;
    return (
      !deepEqual(editedResponse, baselineResponse) ||
      !deepEqual(fieldNotes, baselineFieldNotes) ||
      sessionNotes !== baselineSessionNotes ||
      markAsGolden !== baselineMarkAsGolden
    );
  }, [editedResponse, baselineResponse, fieldNotes, baselineFieldNotes, sessionNotes, baselineSessionNotes, markAsGolden, baselineMarkAsGolden]);

  // Warn about unsaved changes before leaving
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (hasChanges) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasChanges]);

  const loadItem = useCallback(async (itemId) => {
    if (!itemId) {
      setCurrentItem(null);
      setCurrentAnalysis(null);
      return;
    }

    setLoading(true);
    setError(null);
    setSaved(false);

    try {
      // Need raw_response for annotation editing
      const item = await api.getItem(itemId, { includeRawResponse: true });
      setCurrentItem(item);

      // Check if item has an analysis
      if (!item.latest_analysis) {
        setCurrentAnalysis(null);
        setOriginalResponse({});
        setEditedResponse({});
        setBaselineResponse({});
        setFieldNotes({});
        setBaselineFieldNotes({});
        setSessionNotes('');
        setBaselineSessionNotes('');
        setMarkAsGolden(false);
        setBaselineMarkAsGolden(false);
        setError('This item has no analysis yet. Please analyze it first.');
        setLoading(false);
        return;
      }

      setCurrentAnalysis(item.latest_analysis);

      const rawResponse = item.latest_analysis.raw_response || {};
      setOriginalResponse(JSON.parse(JSON.stringify(rawResponse)));

      // Try to load existing annotation (returns null if none exists)
      let loadedFieldNotes = {};
      let loadedSessionNotes = '';
      let loadedMarkAsGolden = false;
      let loadedEditedResponse = JSON.parse(JSON.stringify(rawResponse));

      const annotation = await api.getAnnotationByAnalysis(item.latest_analysis.analysis_id);
      if (annotation) {
        loadedEditedResponse = JSON.parse(JSON.stringify(annotation.annotated_response));
        loadedFieldNotes = annotation.field_notes || {};
        loadedSessionNotes = annotation.session_notes || '';
        loadedMarkAsGolden = annotation.is_golden === 'true';
      }

      // Set current state
      setEditedResponse(loadedEditedResponse);
      setFieldNotes(loadedFieldNotes);
      setSessionNotes(loadedSessionNotes);
      setMarkAsGolden(loadedMarkAsGolden);

      // Set baseline state (for change detection)
      setBaselineResponse(JSON.parse(JSON.stringify(loadedEditedResponse)));
      setBaselineFieldNotes(JSON.parse(JSON.stringify(loadedFieldNotes)));
      setBaselineSessionNotes(loadedSessionNotes);
      setBaselineMarkAsGolden(loadedMarkAsGolden);

      setActiveSection('core');
    } catch (err) {
      setError('Failed to load item: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const saveAnnotation = async () => {
    if (!currentAnalysis) return;

    setSaving(true);
    setError(null);
    setSaved(false);
    setToast(null);

    try {
      await api.saveAnnotation(currentAnalysis.analysis_id, {
        annotated_response: editedResponse,
        field_notes: fieldNotes,
        session_notes: sessionNotes,
        is_golden: markAsGolden ? 'true' : 'false',
      });

      // Update baseline to current state after successful save
      setBaselineResponse(JSON.parse(JSON.stringify(editedResponse)));
      setBaselineFieldNotes(JSON.parse(JSON.stringify(fieldNotes)));
      setBaselineSessionNotes(sessionNotes);
      setBaselineMarkAsGolden(markAsGolden);

      setSaved(true);
      setToast({ message: 'Annotation saved successfully', type: 'success' });
    } catch (err) {
      setError('Failed to save: ' + err.message);
      setToast({ message: 'Failed to save annotation', type: 'error' });
    } finally {
      setSaving(false);
    }
  };


  // Search state for item finder
  const [searchQuery, setSearchQuery] = useState('');
  const [showSearchResults, setShowSearchResults] = useState(false);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  // Filter items based on search query (matches item ID or filename)
  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items;
    const q = searchQuery.toLowerCase().trim();
    return items.filter((item) => {
      const id = (item.id || '').toLowerCase();
      const filename = (item.filename || '').toLowerCase();
      const headline = (item.latest_analysis?.headline || '').toLowerCase();
      return id.includes(q) || filename.includes(q) || headline.includes(q);
    });
  }, [items, searchQuery]);

  // Current item index within the full items list (for prev/next)
  const currentItemIndex = useMemo(() => {
    if (!selectedItemId) return -1;
    return items.findIndex((item) => item.id === selectedItemId);
  }, [items, selectedItemId]);

  const canGoPrev = currentItemIndex > 0;
  const canGoNext = currentItemIndex >= 0 && currentItemIndex < items.length - 1;

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (searchRef.current && !searchRef.current.contains(e.target)) {
        setShowSearchResults(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const navigateToItem = useCallback((itemId) => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to switch items?')) {
        return;
      }
    }
    setMode('annotate');
    setSelectedItemId(itemId);
    setSearchQuery('');
    setShowSearchResults(false);
    loadItem(itemId);
  }, [hasChanges, loadItem]);

  const goToPrev = () => {
    if (canGoPrev) navigateToItem(items[currentItemIndex - 1].id);
  };

  const goToNext = () => {
    if (canGoNext) navigateToItem(items[currentItemIndex + 1].id);
  };

  // Keyboard shortcuts for prev/next and lightbox close
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && showImageLightbox) {
        setShowImageLightbox(false);
        return;
      }
      // Don't navigate when typing in inputs
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.tagName === 'SELECT') return;
      if (e.key === 'ArrowLeft' && canGoPrev) {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'ArrowRight' && canGoNext) {
        e.preventDefault();
        goToNext();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [canGoPrev, canGoNext, currentItemIndex, items, hasChanges, showImageLightbox]);

  const itemLabel = (item) => {
    const analysis = item.latest_analysis;
    if (analysis?.headline) {
      return analysis.headline.length > 50
        ? analysis.headline.substring(0, 50) + '...'
        : analysis.headline;
    }
    return item.filename || item.id;
  };

  const formatItemId = (id) => {
    if (!id) return '';
    return id.length > 8 ? id.substring(0, 8) + '...' : id;
  };

  const isFieldChanged = useCallback(
    (field) => {
      if (!originalResponse || !editedResponse) return false;
      return !deepEqual(originalResponse[field], editedResponse[field]);
    },
    [originalResponse, editedResponse]
  );

  const toggleFieldNote = (field) => {
    setShowingNoteFor(showingNoteFor === field ? null : field);
  };

  const resetChanges = () => {
    if (baselineResponse) {
      setEditedResponse(JSON.parse(JSON.stringify(baselineResponse)));
    }
    setFieldNotes(JSON.parse(JSON.stringify(baselineFieldNotes)));
    setSessionNotes(baselineSessionNotes);
    setMarkAsGolden(baselineMarkAsGolden);
  };

  const updateField = (field, value) => {
    setEditedResponse((prev) => ({ ...prev, [field]: value }));
  };

  const addArrayItem = (field) => {
    setEditedResponse((prev) => ({
      ...prev,
      [field]: [...(prev[field] || []), ''],
    }));
  };

  const updateArrayItem = (field, index, value) => {
    setEditedResponse((prev) => {
      const arr = [...(prev[field] || [])];
      arr[index] = value;
      return { ...prev, [field]: arr };
    });
  };

  const removeArrayItem = (field, index) => {
    setEditedResponse((prev) => ({
      ...prev,
      [field]: prev[field].filter((_, i) => i !== index),
    }));
  };

  const updateNestedField = (parent, key, value) => {
    setEditedResponse((prev) => ({
      ...prev,
      [parent]: { ...prev[parent], [key]: value },
    }));
  };

  const addNestedArrayItem = (parent, key) => {
    setEditedResponse((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: [...(prev[parent]?.[key] || []), ''],
      },
    }));
  };

  const updateNestedArrayItem = (parent, key, index, value) => {
    setEditedResponse((prev) => {
      const arr = [...(prev[parent]?.[key] || [])];
      arr[index] = value;
      return {
        ...prev,
        [parent]: { ...prev[parent], [key]: arr },
      };
    });
  };

  const removeNestedArrayItem = (parent, key, index) => {
    setEditedResponse((prev) => ({
      ...prev,
      [parent]: {
        ...prev[parent],
        [key]: prev[parent][key].filter((_, i) => i !== index),
      },
    }));
  };

  const updateEnrichmentHint = (key, value) => {
    setEditedResponse((prev) => ({
      ...prev,
      enrichment_hint: { ...prev.enrichment_hint, [key]: value },
    }));
  };

  // Primary entity (new schema)
  const updatePrimaryEntity = (key, value) => {
    setEditedResponse((prev) => ({
      ...prev,
      enrichment_hint: {
        ...prev.enrichment_hint,
        primary: { ...prev.enrichment_hint?.primary, [key]: value },
      },
    }));
  };

  // Secondary entities (new schema)
  const addSecondaryEntity = () => {
    setEditedResponse((prev) => ({
      ...prev,
      enrichment_hint: {
        ...prev.enrichment_hint,
        secondary_entities: [
          ...(prev.enrichment_hint?.secondary_entities || []),
          { name: '', entity_type: 'place', subtype: null, schema_org_type: null, confidence: 0.5, hints: [], source_context: null, extracted_url: null },
        ],
      },
    }));
  };

  const updateSecondaryEntity = (index, key, value) => {
    setEditedResponse((prev) => {
      const entities = [...(prev.enrichment_hint?.secondary_entities || [])];
      entities[index] = { ...entities[index], [key]: value };
      return {
        ...prev,
        enrichment_hint: { ...prev.enrichment_hint, secondary_entities: entities },
      };
    });
  };

  const removeSecondaryEntity = (index) => {
    setEditedResponse((prev) => ({
      ...prev,
      enrichment_hint: {
        ...prev.enrichment_hint,
        secondary_entities: (prev.enrichment_hint?.secondary_entities || []).filter((_, i) => i !== index),
      },
    }));
  };

  // Flat entities[] array handlers (new schema)
  const addEntity = () => {
    setEditedResponse((prev) => ({
      ...prev,
      enrichment_hint: {
        ...prev.enrichment_hint,
        entities: [
          ...(prev.enrichment_hint?.entities || []),
          { rank: (prev.enrichment_hint?.entities?.length || 0) + 1, name: '', entity_type: 'place', subtype: null, schema_org_type: null, confidence: 0.5, hints: [], source_context: null, extracted_url: null, extracted_date: null, extracted_location: null },
        ],
      },
    }));
  };

  const updateEntity = (index, key, value) => {
    setEditedResponse((prev) => {
      const entities = [...(prev.enrichment_hint?.entities || [])];
      entities[index] = { ...entities[index], [key]: value };
      return {
        ...prev,
        enrichment_hint: { ...prev.enrichment_hint, entities },
      };
    });
  };

  const removeEntity = (index) => {
    setEditedResponse((prev) => ({
      ...prev,
      enrichment_hint: {
        ...prev.enrichment_hint,
        entities: (prev.enrichment_hint?.entities || []).filter((_, i) => i !== index),
      },
    }));
  };


  const copyJson = () => {
    navigator.clipboard.writeText(JSON.stringify(originalResponse, null, 2));
  };

  const handleLogout = () => {
    if (hasChanges) {
      if (!window.confirm('You have unsaved changes. Are you sure you want to logout?')) {
        return;
      }
    }
    logout();
  };

  // Reusable left panel content for both annotate and compare modes
  const renderLeftPanel = ({ item, analysisResponse, analysis }) => (
    <div className="review-panel-content">
      {/* Image */}
      <div className="review-image-container">
        <button
          type="button"
          className="review-image-btn"
          onClick={() => setShowImageLightbox(true)}
          aria-label="View full-size image"
        >
          <img
            src={item.image}
            alt="Item preview"
            className="review-image"
            onError={(e) => {
              e.target.src = "data:image/svg+xml,<svg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 100 100'><rect fill='%23f3f4f6' width='100' height='100'/><text x='50' y='50' text-anchor='middle' dy='.3em' fill='%239ca3af'>No Image</text></svg>";
            }}
          />
        </button>
      </div>

      {/* Headline */}
      <div className="review-field-display">
        <label className="review-field-label">Headline</label>
        <p className="review-field-value review-field-value-lg">
          {analysisResponse?.headline || 'No headline'}
        </p>
      </div>

      {/* Summary */}
      <div className="review-field-display">
        <label className="review-field-label">Summary</label>
        <p className="review-field-value">
          {analysisResponse?.summary || 'No summary'}
        </p>
      </div>

      {/* Category */}
      <div className="review-field-display">
        <label className="review-field-label">Category</label>
        <span className="review-category-badge">
          {analysisResponse?.category || 'Uncategorized'}
        </span>
      </div>

      {/* Metadata */}
      <div className="review-metadata">
        <p><span className="review-metadata-label">Item ID:</span> {item.id}</p>
        <p><span className="review-metadata-label">Analysis ID:</span> {analysis?.analysis_id}</p>
        <p><span className="review-metadata-label">Model:</span> {analysis?.model_used}</p>
        <p><span className="review-metadata-label">Provider:</span> {analysis?.provider_used}</p>
        {analysis?.trace_id && (
          <p>
            <span className="review-metadata-label">Trace:</span>{' '}
            <a
              href={`https://smith.langchain.com/public/${analysis.trace_id}`}
              target="_blank"
              rel="noopener noreferrer"
              className="review-link"
            >
              View in LangSmith
            </a>
          </p>
        )}
      </div>
    </div>
  );

  return (
    <div className="review-page">
      {/* Header */}
      <header className="review-header">
        <div className="review-header-content">
          <div className="review-header-left">
            <Link to="/" className="review-back-link" aria-label="Back to Gallery">
              <ArrowLeft className="review-icon" />
            </Link>
            <h1 className="review-title">Analysis Review Tool</h1>
          </div>
          <div className="review-header-right">
            {loading && <span className="review-status">{loadProgress || 'Loading...'}</span>}
            {saved && <span className="review-status review-status-success">Saved!</span>}
            {error && <span className="review-status review-status-error">{error}</span>}
            <Link to="/rankings" className="review-dashboard-link">Dashboard</Link>
            <span className="review-build-info">
              {import.meta.env.VITE_GIT_SHA?.slice(0, 7) || ''}
              {import.meta.env.VITE_BUILD_TIME ? ` · ${new Date(import.meta.env.VITE_BUILD_TIME).toLocaleDateString()}` : ''}
            </span>
            <button
              onClick={handleLogout}
              className="review-logout-btn"
              aria-label="Logout"
            >
              <SignOut className="review-icon-sm" />
              Logout
            </button>
          </div>
        </div>
      </header>

      {/* Item Finder */}
      <div className="review-selector">
        <div className="review-selector-content">
          {/* Search */}
          <div className="review-search-wrap" ref={searchRef}>
            <MagnifyingGlass className="review-search-icon" />
            <input
              ref={searchInputRef}
              type="text"
              value={searchQuery}
              onChange={(e) => {
                setSearchQuery(e.target.value);
                setShowSearchResults(true);
              }}
              onFocus={() => setShowSearchResults(true)}
              placeholder="Search by ID, filename, or headline..."
              className="review-search-input"
              aria-label="Search items"
              autoComplete="off"
            />
            {searchQuery && (
              <button
                className="review-search-clear"
                onClick={() => { setSearchQuery(''); searchInputRef.current?.focus(); }}
                aria-label="Clear search"
              >
                <X className="review-icon-sm" />
              </button>
            )}
            {showSearchResults && searchQuery.trim() && (
              <ul className="review-search-results" role="listbox">
                {filteredItems.length === 0 ? (
                  <li className="review-search-empty">No items match "{searchQuery}"</li>
                ) : (
                  filteredItems.slice(0, 20).map((item) => (
                    <li
                      key={item.id}
                      role="option"
                      aria-selected={item.id === selectedItemId}
                      className={`review-search-result ${item.id === selectedItemId ? 'review-search-result-active' : ''}`}
                      onMouseDown={() => navigateToItem(item.id)}
                    >
                      <span className="review-search-result-label">{itemLabel(item)}</span>
                      <span className="review-search-result-meta">
                        {item.filename && <span className="review-search-result-filename">{item.filename}</span>}
                        <span className="review-search-result-id">{formatItemId(item.id)}</span>
                      </span>
                    </li>
                  ))
                )}
                {filteredItems.length > 20 && (
                  <li className="review-search-empty">
                    {filteredItems.length - 20} more results. Refine your search
                  </li>
                )}
              </ul>
            )}
          </div>

          {/* Prev / Next navigation */}
          <div className="review-nav-group">
            <button
              onClick={goToPrev}
              disabled={!canGoPrev}
              className="review-nav-btn"
              aria-label="Previous item"
              title="Previous item (←)"
            >
              <CaretLeft className="review-icon-sm" />
              Prev
            </button>
            {selectedItemId && (
              <span className="review-nav-position">
                {currentItemIndex + 1} / {items.length}
              </span>
            )}
            <button
              onClick={goToNext}
              disabled={!canGoNext}
              className="review-nav-btn"
              aria-label="Next item"
              title="Next item (→)"
            >
              Next
              <CaretRight className="review-icon-sm" />
            </button>
          </div>

          {/* Refresh + count */}
          <button
            onClick={loadItems}
            className="review-refresh-btn"
            aria-label="Refresh item list"
          >
            Refresh
          </button>
          <span className="review-item-count">{items.length} items</span>

          {/* Mode switcher */}
          {currentItem && (
            <div className="review-mode-switcher">
              <button
                className={`review-mode-btn ${mode === 'annotate' ? 'review-mode-btn-active' : ''}`}
                onClick={() => {
                  if (mode === 'annotate') return;
                  setMode('annotate');
                }}
              >
                Annotate
              </button>
              <button
                className={`review-mode-btn ${mode === 'compare' ? 'review-mode-btn-active' : ''}`}
                onClick={() => {
                  if (mode === 'compare') return;
                  if (hasChanges && !window.confirm('You have unsaved annotation changes. Switch to compare mode?')) return;
                  setMode('compare');
                }}
              >
                Compare & Rank
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Compare Mode — 2-panel layout: image left, compare cards right */}
      {currentItem && mode === 'compare' && (
        <main className="review-main">
          <div className="review-left-panel">
            {renderLeftPanel({
              item: currentItem,
              analysisResponse: currentAnalysis?.raw_response,
              analysis: currentAnalysis,
            })}
          </div>
          <div className="review-right-panel">
            <AnalysisCompare item={currentItem} onClose={() => setMode('annotate')} />
          </div>
        </main>
      )}

      {/* Main Content (Annotate Mode) */}
      {currentItem && currentAnalysis && mode === 'annotate' && (
        <main className="review-main">
          {/* Left Panel: Image + Summary (1/3) */}
          <div className="review-left-panel">
            {renderLeftPanel({
              item: currentItem,
              analysisResponse: editedResponse,
              analysis: currentAnalysis,
            })}
          </div>

          {/* Right Panel: Analysis Editor (2/3) */}
          <div className="review-right-panel">
            {/* Horizontal Tabs */}
            <div className="review-tabs">
              {sections.map((section) => (
                <button
                  key={section.key}
                  onClick={() => setActiveSection(section.key)}
                  className={`review-tab ${activeSection === section.key ? 'review-tab-active' : ''}`}
                  aria-selected={activeSection === section.key}
                  role="tab"
                >
                  {section.label}
                </button>
              ))}
            </div>

            {/* Section Content */}
            <div className="review-section-content" role="tabpanel">
              {/* Core Fields Section - dynamically displays all non-object fields */}
              {activeSection === 'core' && (
                <div className="review-fields">
                  {/* Primitive fields (strings, numbers, booleans, null) */}
                  {primitiveFields.map((field) => (
                      <div key={field} className="review-field-card">
                        <div className="review-field-header">
                          <label htmlFor={`field-${field}`} className="review-field-name">{field}</label>
                          <div className="review-field-actions">
                            {isFieldChanged(field) && (
                              <span className="review-modified-badge">Modified</span>
                            )}
                            <button
                              onClick={() => toggleFieldNote(field)}
                              className={`review-note-btn ${fieldNotes[field] ? 'review-note-btn-active' : ''}`}
                              aria-label={`Add note for ${field}`}
                              aria-pressed={showingNoteFor === field}
                            >
                              <NotePencil className="review-icon-sm" />
                            </button>
                          </div>
                        </div>
                        {/* Use textarea for long text fields */}
                        {['summary', 'description', 'extracted_text', 'rationale'].includes(field) ||
                         (typeof editedResponse?.[field] === 'string' && editedResponse[field]?.length > 100) ? (
                          <textarea
                            id={`field-${field}`}
                            value={editedResponse?.[field] ?? ''}
                            onChange={(e) => updateField(field, e.target.value)}
                            rows={3}
                            className={`review-input review-textarea ${isFieldChanged(field) ? 'review-input-modified' : ''}`}
                          />
                        ) : typeof editedResponse?.[field] === 'boolean' ? (
                          <select
                            id={`field-${field}`}
                            value={editedResponse?.[field] ? 'true' : 'false'}
                            onChange={(e) => updateField(field, e.target.value === 'true')}
                            className={`review-select-small ${isFieldChanged(field) ? 'review-input-modified' : ''}`}
                          >
                            <option value="true">true</option>
                            <option value="false">false</option>
                          </select>
                        ) : typeof editedResponse?.[field] === 'number' ? (
                          <input
                            id={`field-${field}`}
                            type="number"
                            step="any"
                            value={editedResponse?.[field] ?? ''}
                            onChange={(e) => updateField(field, e.target.value === '' ? null : parseFloat(e.target.value))}
                            className={`review-input ${isFieldChanged(field) ? 'review-input-modified' : ''}`}
                          />
                        ) : ENUM_FIELDS[field] ? (
                          <select
                            id={`field-${field}`}
                            value={editedResponse?.[field] ?? ''}
                            onChange={(e) => updateField(field, e.target.value)}
                            className={`review-select-small ${isFieldChanged(field) ? 'review-input-modified' : ''}`}
                          >
                            <option value="">-- select --</option>
                            {ENUM_FIELDS[field].map((opt) => (
                              <option key={opt} value={opt}>{opt}</option>
                            ))}
                          </select>
                        ) : (
                          <input
                            id={`field-${field}`}
                            type="text"
                            value={editedResponse?.[field] ?? ''}
                            onChange={(e) => updateField(field, e.target.value)}
                            placeholder={editedResponse?.[field] === null ? 'null' : ''}
                            className={`review-input ${isFieldChanged(field) ? 'review-input-modified' : ''}`}
                          />
                        )}
                        {showingNoteFor === field && (
                          <div className="review-note-input-container">
                            <input
                              type="text"
                              value={fieldNotes[field] || ''}
                              onChange={(e) => setFieldNotes({ ...fieldNotes, [field]: e.target.value })}
                              placeholder="Add note explaining this change..."
                              className="review-input review-note-input"
                              aria-label={`Note for ${field}`}
                            />
                          </div>
                        )}
                      </div>
                    ))}

                  {/* Array fields */}
                  {arrayFields.map((field) => (
                      <div key={field} className="review-field-card">
                        <div className="review-field-header">
                          <label className="review-field-name">{field}</label>
                          <button
                            onClick={() => addArrayItem(field)}
                            className="review-add-btn"
                            aria-label={`Add ${field} item`}
                          >
                            + Add
                          </button>
                        </div>
                        <div className="review-array-items">
                          {(editedResponse?.[field] || []).map((item, index) => (
                            <div key={index} className="review-array-item">
                              <input
                                type="text"
                                value={typeof item === 'object' ? JSON.stringify(item) : item}
                                onChange={(e) => updateArrayItem(field, index, e.target.value)}
                                className="review-input review-input-flex"
                                aria-label={`${field} item ${index + 1}`}
                              />
                              <button
                                onClick={() => removeArrayItem(field, index)}
                                className="review-remove-btn"
                                aria-label={`Remove ${field} item ${index + 1}`}
                              >
                                <Minus className="review-icon-sm" />
                              </button>
                            </div>
                          ))}
                          {(!editedResponse?.[field] || editedResponse[field].length === 0) && (
                            <p className="review-empty-text">No items</p>
                          )}
                        </div>
                      </div>
                    ))}
                </div>
              )}

              {/* Media Metadata Section */}
              {activeSection === 'media_metadata' && editedResponse?.media_metadata && (
                <div className="review-fields">
                  {Object.entries(editedResponse.media_metadata || {}).map(([key, value]) => (
                    <div key={key} className="review-field-card">
                      <div className="review-field-header">
                        <label className="review-field-name">{key}</label>
                        {Array.isArray(value) && (
                          <button
                            onClick={() => addNestedArrayItem('media_metadata', key)}
                            className="review-add-btn"
                            aria-label={`Add ${key} item`}
                          >
                            + Add
                          </button>
                        )}
                      </div>
                      {Array.isArray(value) ? (
                        <div className="review-array-items">
                          {value.map((item, index) => (
                            <div key={index} className="review-array-item">
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => updateNestedArrayItem('media_metadata', key, index, e.target.value)}
                                className="review-input review-input-flex"
                                aria-label={`${key} item ${index + 1}`}
                              />
                              <button
                                onClick={() => removeNestedArrayItem('media_metadata', key, index)}
                                className="review-remove-btn"
                                aria-label={`Remove ${key} item ${index + 1}`}
                              >
                                <Minus className="review-icon-sm" />
                              </button>
                            </div>
                          ))}
                          {value.length === 0 && <p className="review-empty-text">No items</p>}
                        </div>
                      ) : (
                        <input
                          type="text"
                          value={value ?? ''}
                          onChange={(e) => updateNestedField('media_metadata', key, e.target.value)}
                          placeholder={value === null ? 'null' : ''}
                          className="review-input"
                          aria-label={key}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Image Details Section */}
              {activeSection === 'image_details' && editedResponse?.image_details && (
                <div className="review-fields">
                  {Object.entries(editedResponse.image_details || {}).map(([key, value]) => (
                    <div key={key} className="review-field-card">
                      <div className="review-field-header">
                        <label className="review-field-name">{key}</label>
                        {Array.isArray(value) && (
                          <button
                            onClick={() => addNestedArrayItem('image_details', key)}
                            className="review-add-btn"
                            aria-label={`Add ${key} item`}
                          >
                            + Add
                          </button>
                        )}
                      </div>
                      {Array.isArray(value) ? (
                        <div className="review-array-items">
                          {value.map((item, index) => (
                            <div key={index} className="review-array-item">
                              <input
                                type="text"
                                value={item}
                                onChange={(e) => updateNestedArrayItem('image_details', key, index, e.target.value)}
                                className="review-input review-input-flex"
                                aria-label={`${key} item ${index + 1}`}
                              />
                              <button
                                onClick={() => removeNestedArrayItem('image_details', key, index)}
                                className="review-remove-btn"
                                aria-label={`Remove ${key} item ${index + 1}`}
                              >
                                <Minus className="review-icon-sm" />
                              </button>
                            </div>
                          ))}
                          {value.length === 0 && <p className="review-empty-text">No items</p>}
                        </div>
                      ) : ENUM_FIELDS[key] ? (
                        <select
                          value={value ?? ''}
                          onChange={(e) => updateNestedField('image_details', key, e.target.value)}
                          className="review-select-small"
                          aria-label={key}
                        >
                          <option value="">-- select --</option>
                          {ENUM_FIELDS[key].map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={value ?? ''}
                          onChange={(e) => updateNestedField('image_details', key, e.target.value)}
                          className="review-input"
                          aria-label={key}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Enrichment Hint Section */}
              {activeSection === 'enrichment_hint' && editedResponse?.enrichment_hint && (() => {
                const eh = editedResponse.enrichment_hint;
                // Reusable entity field renderer
                const renderEntityFields = (entity, label, onUpdate, onRemove) => (
                  <div
                    className={`review-entity-card ${!onRemove ? 'review-entity-primary' : ''}`}
                  >
                    <div className="review-entity-header">
                      <span className={`review-entity-label ${!onRemove ? 'review-entity-label-primary' : ''}`}>
                        {label}
                      </span>
                      {onRemove && (
                        <button onClick={onRemove} className="review-remove-text-btn" aria-label={`Remove ${label}`}>
                          Remove
                        </button>
                      )}
                    </div>
                    {/* name (full width) */}
                    <div className="review-entity-fields-full">
                      <div className="review-entity-field">
                        <label className="review-nested-label">name</label>
                        <input
                          type="text"
                          value={entity.name || ''}
                          onChange={(e) => onUpdate('name', e.target.value)}
                          className="review-input review-input-small"
                        />
                      </div>
                    </div>
                    {/* entity_type | schema_org_type | subtype */}
                    <div className="review-entity-fields" style={{ marginTop: '0.5rem' }}>
                      <div className="review-entity-field">
                        <label className="review-nested-label">entity_type</label>
                        <select
                          value={entity.entity_type || ''}
                          onChange={(e) => onUpdate('entity_type', e.target.value)}
                          className="review-select-small"
                        >
                          {ENTITY_TYPES.map((type) => (
                            <option key={type} value={type}>{type}</option>
                          ))}
                        </select>
                      </div>
                      <div className="review-entity-field">
                        <label className="review-nested-label">schema_org_type</label>
                        <input
                          type="text"
                          value={entity.schema_org_type ?? ''}
                          onChange={(e) => onUpdate('schema_org_type', e.target.value || null)}
                          className="review-input review-input-small"
                          placeholder="e.g. Restaurant"
                        />
                      </div>
                      <div className="review-entity-field">
                        <label className="review-nested-label">subtype</label>
                        <input
                          type="text"
                          value={entity.subtype ?? ''}
                          onChange={(e) => onUpdate('subtype', e.target.value || null)}
                          className="review-input review-input-small"
                          placeholder="e.g. Italian Restaurant"
                          list="subtype-suggestions"
                        />
                      </div>
                    </div>
                    {/* confidence */}
                    <div className="review-entity-fields-full" style={{ marginTop: '0.5rem' }}>
                      <div className="review-entity-field">
                        <label className="review-nested-label">confidence</label>
                        <div className="review-range-container">
                          <input
                            type="range" min="0" max="1" step="0.05"
                            value={entity.confidence || 0}
                            onChange={(e) => onUpdate('confidence', parseFloat(e.target.value))}
                            className="review-range"
                          />
                          <span className="review-range-value-small">{entity.confidence}</span>
                        </div>
                      </div>
                    </div>
                    {/* hints */}
                    <div className="review-entity-fields-full" style={{ marginTop: '0.5rem' }}>
                      <div className="review-entity-field">
                        <label className="review-nested-label">hints</label>
                        <div className="review-entity-hints">
                          {(entity.hints || []).map((hint, hintIndex) => (
                            <span key={hintIndex} className="review-entity-hint-tag">
                              {hint}
                              <button
                                type="button"
                                className="review-entity-hint-remove"
                                onClick={() => {
                                  const newHints = [...(entity.hints || [])];
                                  newHints.splice(hintIndex, 1);
                                  onUpdate('hints', newHints);
                                }}
                                aria-label={`Remove hint "${hint}"`}
                              >
                                ×
                              </button>
                            </span>
                          ))}
                          <div className="review-entity-hint-add">
                            <input
                              type="text"
                              placeholder="Add hint..."
                              className="review-input review-input-small"
                              onKeyDown={(e) => {
                                if (e.key === 'Enter' && e.target.value.trim()) {
                                  onUpdate('hints', [...(entity.hints || []), e.target.value.trim()]);
                                  e.target.value = '';
                                }
                              }}
                            />
                            <button
                              type="button"
                              className="review-add-btn"
                              style={{ fontSize: '0.7rem', padding: '0.2rem 0.5rem' }}
                              onClick={(e) => {
                                const input = e.target.previousElementSibling;
                                if (input.value.trim()) {
                                  onUpdate('hints', [...(entity.hints || []), input.value.trim()]);
                                  input.value = '';
                                }
                              }}
                            >
                              + Add
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                    {/* source_context | extracted_url */}
                    <div className="review-entity-fields" style={{ marginTop: '0.5rem' }}>
                      <div className="review-entity-field">
                        <label className="review-nested-label">source_context</label>
                        <input
                          type="text"
                          value={entity.source_context ?? ''}
                          onChange={(e) => onUpdate('source_context', e.target.value || null)}
                          className="review-input review-input-small"
                          placeholder="null"
                        />
                      </div>
                      <div className="review-entity-field">
                        <label className="review-nested-label">extracted_url</label>
                        <input
                          type="url"
                          value={entity.extracted_url ?? ''}
                          onChange={(e) => onUpdate('extracted_url', e.target.value || null)}
                          className="review-input review-input-small"
                          placeholder="null"
                        />
                      </div>
                    </div>
                    {/* extracted_date | extracted_location */}
                    <div className="review-entity-fields" style={{ marginTop: '0.5rem' }}>
                      <div className="review-entity-field">
                        <label className="review-nested-label">extracted_date</label>
                        <input
                          type="text"
                          value={entity.extracted_date ?? ''}
                          onChange={(e) => onUpdate('extracted_date', e.target.value || null)}
                          className="review-input review-input-small"
                          placeholder="null"
                        />
                      </div>
                      <div className="review-entity-field">
                        <label className="review-nested-label">extracted_location</label>
                        <input
                          type="text"
                          value={entity.extracted_location ?? ''}
                          onChange={(e) => onUpdate('extracted_location', e.target.value || null)}
                          className="review-input review-input-small"
                          placeholder="null"
                        />
                      </div>
                    </div>
                  </div>
                );

                return (
                <div className="review-fields">
                  {/* Confidence */}
                  <div className="review-field-card">
                    <label htmlFor="confidence" className="review-field-name">confidence</label>
                    <input
                      id="confidence"
                      type="range" min="0" max="1" step="0.05"
                      value={eh.confidence || 0}
                      onChange={(e) => updateEnrichmentHint('confidence', parseFloat(e.target.value))}
                      className="review-range"
                    />
                    <span className="review-range-value">{eh.confidence}</span>
                  </div>

                  {/* Ambiguous */}
                  <div className="review-field-card">
                    <label htmlFor="ambiguous" className="review-field-name">ambiguous</label>
                    <select
                      id="ambiguous"
                      value={eh.ambiguous ? 'true' : 'false'}
                      onChange={(e) => updateEnrichmentHint('ambiguous', e.target.value === 'true')}
                      className="review-select-small"
                    >
                      <option value="true">true</option>
                      <option value="false">false</option>
                    </select>
                  </div>

                  {/* Entity Count */}
                  {eh.entity_count != null && (
                    <div className="review-field-card">
                      <label htmlFor="entity_count" className="review-field-name">entity_count</label>
                      <input
                        id="entity_count"
                        type="number" min="0"
                        value={eh.entity_count || 0}
                        onChange={(e) => updateEnrichmentHint('entity_count', parseInt(e.target.value) || 0)}
                        className="review-input review-input-small"
                        style={{ width: '80px' }}
                      />
                    </div>
                  )}

                  {/* Entities — detect flat entities[] array vs primary/secondary_entities schema */}
                  {Array.isArray(eh.entities) ? (
                    /* Flat entities[] array schema */
                    <div className="review-field-card">
                      <div className="review-field-header">
                        <h3 className="review-section-title">Entities</h3>
                        <button onClick={addEntity} className="review-add-btn" aria-label="Add entity">
                          + Add Entity
                        </button>
                      </div>
                      {eh.entities.map((entity, idx) => (
                        <React.Fragment key={idx}>
                          {renderEntityFields(
                            entity,
                            entity.rank === 1 ? 'Primary (rank 1)' : `Entity #${idx + 1} (rank ${entity.rank ?? idx + 1})`,
                            (key, value) => updateEntity(idx, key, value),
                            eh.entities.length > 1 ? () => removeEntity(idx) : null
                          )}
                        </React.Fragment>
                      ))}
                      {eh.entities.length === 0 && (
                        <p className="review-empty-text">No entities</p>
                      )}
                    </div>
                  ) : (
                    /* Legacy primary/secondary_entities schema */
                    <>
                      <div className="review-field-card">
                        <h3 className="review-section-title">Primary Entity</h3>
                        {eh.primary ? (
                          renderEntityFields(
                            eh.primary,
                            'Primary',
                            (key, value) => updatePrimaryEntity(key, value),
                            null
                          )
                        ) : (
                          <p className="review-empty-text">No primary entity</p>
                        )}
                      </div>

                      <div className="review-field-card">
                        <div className="review-field-header">
                          <h3 className="review-section-title">Secondary Entities</h3>
                          <button onClick={addSecondaryEntity} className="review-add-btn" aria-label="Add secondary entity">
                            + Add Entity
                          </button>
                        </div>
                        {(eh.secondary_entities || []).map((entity, idx) => (
                          <React.Fragment key={idx}>
                            {renderEntityFields(
                              entity,
                              `Secondary #${idx + 1}`,
                              (key, value) => updateSecondaryEntity(idx, key, value),
                              () => removeSecondaryEntity(idx)
                            )}
                          </React.Fragment>
                        ))}
                        {(!eh.secondary_entities || eh.secondary_entities.length === 0) && (
                          <p className="review-empty-text">No secondary entities</p>
                        )}
                      </div>
                    </>
                  )}

                  {/* Classification Rationale */}
                  <div className="review-field-card">
                    <label htmlFor="classification_rationale" className="review-field-name">classification_rationale</label>
                    <textarea
                      id="classification_rationale"
                      value={eh.classification_rationale || ''}
                      onChange={(e) => updateEnrichmentHint('classification_rationale', e.target.value)}
                      rows={2}
                      className="review-input review-textarea"
                    />
                  </div>

                  {/* Subtype suggestions datalist */}
                  <datalist id="subtype-suggestions">
                    {SUBTYPE_SUGGESTIONS.map((s) => (
                      <option key={s} value={s} />
                    ))}
                  </datalist>
                </div>
                );
              })()}

              {/* Generic section for any other nested objects */}
              {activeSection !== 'core' &&
               activeSection !== 'raw' &&
               !['media_metadata', 'image_details', 'enrichment_hint'].includes(activeSection) &&
               editedResponse?.[activeSection] &&
               typeof editedResponse[activeSection] === 'object' && (
                <div className="review-fields">
                  {Object.entries(editedResponse[activeSection] || {}).map(([key, value]) => (
                    <div key={key} className="review-field-card">
                      <div className="review-field-header">
                        <label className="review-field-name">{key}</label>
                        {Array.isArray(value) && (
                          <button
                            onClick={() => addNestedArrayItem(activeSection, key)}
                            className="review-add-btn"
                            aria-label={`Add ${key} item`}
                          >
                            + Add
                          </button>
                        )}
                      </div>
                      {Array.isArray(value) ? (
                        <div className="review-array-items">
                          {value.map((item, index) => (
                            <div key={index} className="review-array-item">
                              <input
                                type="text"
                                value={typeof item === 'object' ? JSON.stringify(item) : item}
                                onChange={(e) => updateNestedArrayItem(activeSection, key, index, e.target.value)}
                                className="review-input review-input-flex"
                                aria-label={`${key} item ${index + 1}`}
                              />
                              <button
                                onClick={() => removeNestedArrayItem(activeSection, key, index)}
                                className="review-remove-btn"
                                aria-label={`Remove ${key} item ${index + 1}`}
                              >
                                <Minus className="review-icon-sm" />
                              </button>
                            </div>
                          ))}
                          {value.length === 0 && <p className="review-empty-text">No items</p>}
                        </div>
                      ) : typeof value === 'object' && value !== null ? (
                        <textarea
                          value={JSON.stringify(value, null, 2)}
                          onChange={(e) => {
                            try {
                              updateNestedField(activeSection, key, JSON.parse(e.target.value));
                            } catch {
                              // Keep raw text while editing
                            }
                          }}
                          rows={4}
                          className="review-input review-textarea"
                          aria-label={key}
                        />
                      ) : typeof value === 'boolean' ? (
                        <select
                          value={value ? 'true' : 'false'}
                          onChange={(e) => updateNestedField(activeSection, key, e.target.value === 'true')}
                          className="review-select-small"
                          aria-label={key}
                        >
                          <option value="true">true</option>
                          <option value="false">false</option>
                        </select>
                      ) : typeof value === 'number' ? (
                        <input
                          type="number"
                          step="any"
                          value={value ?? ''}
                          onChange={(e) => updateNestedField(activeSection, key, e.target.value === '' ? null : parseFloat(e.target.value))}
                          className="review-input"
                          aria-label={key}
                        />
                      ) : ENUM_FIELDS[key] ? (
                        <select
                          value={value ?? ''}
                          onChange={(e) => updateNestedField(activeSection, key, e.target.value)}
                          className="review-select-small"
                          aria-label={key}
                        >
                          <option value="">-- select --</option>
                          {ENUM_FIELDS[key].map((opt) => (
                            <option key={opt} value={opt}>{opt}</option>
                          ))}
                        </select>
                      ) : (
                        <input
                          type="text"
                          value={value ?? ''}
                          onChange={(e) => updateNestedField(activeSection, key, e.target.value)}
                          placeholder={value === null ? 'null' : ''}
                          className="review-input"
                          aria-label={key}
                        />
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Raw JSON Section */}
              {activeSection === 'raw' && (
                <div className="review-json-container">
                  <div className="review-field-header" style={{ marginBottom: 'var(--space-sm)' }}>
                    <label className="review-field-name">Raw JSON (read-only reference)</label>
                    <button
                      onClick={copyJson}
                      className="review-add-btn"
                      aria-label="Copy JSON to clipboard"
                    >
                      Copy
                    </button>
                  </div>
                  <pre className="review-json">
                    {JSON.stringify(originalResponse, null, 2)}
                  </pre>
                </div>
              )}
            </div>

            {/* Footer: Session Notes + Actions */}
            <div className="review-footer">
              <div className="review-footer-notes">
                <label htmlFor="session-notes" className="review-field-name">Session Notes</label>
                <textarea
                  id="session-notes"
                  value={sessionNotes}
                  onChange={(e) => setSessionNotes(e.target.value)}
                  rows={2}
                  placeholder="Add overall notes about this annotation session..."
                  className="review-input review-textarea"
                />
              </div>
              <div className="review-footer-actions">
                <div className="review-footer-left">
                  <label className="review-checkbox-label">
                    <input
                      type="checkbox"
                      checked={markAsGolden}
                      onChange={(e) => setMarkAsGolden(e.target.checked)}
                      className="review-checkbox"
                    />
                    <span>Mark as Golden</span>
                  </label>
                  {hasChanges && (
                    <span className="review-unsaved-badge">Unsaved changes</span>
                  )}
                </div>
                <div className="review-footer-right">
                  <button
                    onClick={resetChanges}
                    disabled={!hasChanges}
                    className="btn btn-secondary"
                  >
                    Reset
                  </button>
                  <button
                    onClick={saveAnnotation}
                    disabled={saving || !hasChanges}
                    className="btn btn-primary"
                  >
                    {saving ? 'Saving...' : 'Save Annotation'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </main>
      )}

      {/* No Analysis State */}
      {currentItem && !currentAnalysis && !loading && (
        <div className="review-empty-state">
          <Lightbulb className="review-empty-icon" />
          <p className="review-empty-title">No analysis available</p>
          <p className="review-empty-description">This item needs to be analyzed before it can be reviewed.</p>
        </div>
      )}

      {/* Thumbnail Grid (when no item selected) */}
      {!currentItem && !loading && items.length > 0 && (
        <div className="review-thumbnail-grid">
          {items.map((item) => (
            <button
              key={item.id}
              className={`review-thumbnail ${item.id === selectedItemId ? 'review-thumbnail-active' : ''}`}
              onClick={() => navigateToItem(item.id)}
              title={item.latest_analysis?.headline || item.original_filename || item.id}
            >
              {item.image ? (
                <img
                  src={item.image}
                  alt={item.latest_analysis?.headline || 'Item thumbnail'}
                  loading="lazy"
                />
              ) : (
                <Image size={32} />
              )}
            </button>
          ))}
        </div>
      )}

      {/* Empty State (no items at all) */}
      {!currentItem && !loading && items.length === 0 && (
        <div className="review-empty-state">
          <ImageSquare className="review-empty-icon" />
          <p className="review-empty-title">No items found</p>
          <p className="review-empty-description">Upload some images to get started</p>
        </div>
      )}

      {/* Toast Notification */}
      <Toast toast={toast} />

      {/* Image Lightbox */}
      {showImageLightbox && currentItem?.image && (
        <div
          className="review-lightbox"
          onClick={() => setShowImageLightbox(false)}
          role="dialog"
          aria-label="Full-size image"
        >
          <button
            className="review-lightbox-close"
            onClick={() => setShowImageLightbox(false)}
            aria-label="Close lightbox"
          >
            <X size={24} />
          </button>
          <img
            src={currentItem.image}
            alt="Full-size preview"
            className="review-lightbox-image"
            onClick={(e) => e.stopPropagation()}
          />
        </div>
      )}
    </div>
  );
}
